/**
 * Barkod / QR tarayıcı (mobil, tam ekran overlay).
 * Kamera: native BarcodeDetector API (Android Chrome/Edge) + environment kamera; desteklenmiyorsa
 * yalnız manuel giriş. Taranan/girilen kod inventory.resolveScan ile çözülür → lot/sipariş özeti +
 * "Aç" aksiyonu (lot → /inventory?trace=; sipariş → /orders). Kamera izni yoksa manuel akış çalışır.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { useEsc } from "./primitives";

// BarcodeDetector deneysel API — TS lib'inde yok.
type BarcodeDetectorLike = { detect: (src: CanvasImageSource) => Promise<Array<{ rawValue: string }>> };
interface BarcodeDetectorCtor { new (opts?: { formats?: string[] }): BarcodeDetectorLike }

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
const LOT_STATUS_TR: Record<string, { label: string; cls: string }> = {
  released: { label: "Serbest", cls: "b-success" },
  pending_qc: { label: "QC Bekliyor", cls: "b-warning" },
  on_hold: { label: "Blokeli", cls: "b-warning" },
  expired: { label: "SKT Geçti", cls: "b-danger" },
  rejected: { label: "Reddedildi", cls: "b-danger" },
  consumed: { label: "Tükendi", cls: "b-neutral" },
};

export function ScannerOverlay({
  onClose,
  onLot,
  title = "Barkod / QR Tara",
}: {
  onClose: () => void;
  /** Verilirse lot çözülünce navigasyon yerine bu çağrılır (ör. QC işleme akışı). */
  onLot?: (lot: { lotId: string; lotCode: string; status: string }) => void;
  title?: string;
}) {
  const navigate = useNavigate();
  useEsc(true, onClose);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const [manual, setManual] = useState("");
  const [code, setCode] = useState<string | null>(null); // çözülecek kod (kamera/manuel)
  const [camErr, setCamErr] = useState<string | null>(null);
  const supported = typeof window !== "undefined" && "BarcodeDetector" in window;

  const resolveQ = trpc.inventory.resolveScan.useQuery({ code: code ?? "" }, { enabled: !!code, staleTime: 0 });
  const r = resolveQ.data;

  function stopCam() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  // Kamera + BarcodeDetector tarama döngüsü (yalnız desteklenirse ve henüz kod yokken)
  useEffect(() => {
    if (!supported || code) return;
    let cancelled = false;
    (async () => {
      try {
        const Ctor = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor }).BarcodeDetector;
        const detector = new Ctor({ formats: ["qr_code", "code_128", "ean_13", "code_39", "data_matrix", "codabar", "itf"] });
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        const loop = async () => {
          if (cancelled || !videoRef.current) return;
          try {
            const found = await detector.detect(videoRef.current);
            if (found && found[0]?.rawValue) { setCode(found[0].rawValue.trim()); return; }
          } catch { /* kare hatası — döngüye devam */ }
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      } catch (e) {
        setCamErr((e as Error)?.message || "Kamera açılamadı — kodu elle girebilirsiniz.");
      }
    })();
    return () => { cancelled = true; stopCam(); };
  }, [supported, code]);

  useEffect(() => () => stopCam(), []);

  function submitManual() {
    const c = manual.trim();
    if (c) setCode(c);
  }
  function reset() { setCode(null); setManual(""); }
  function act() {
    if (!r) return;
    stopCam();
    if (r.type === "lot") { onClose(); navigate(`/inventory?trace=${r.lotId}`); }
    else if (r.type === "order") { onClose(); navigate(`/orders?q=${encodeURIComponent(r.orderCode)}`); }
  }

  return (
    <div className="m-scanner" role="dialog" aria-modal="true" aria-label="Barkod / QR tara">
      <div className="m-scan-top">
        <button type="button" className="m-iconbtn" onClick={() => { stopCam(); onClose(); }} aria-label="Kapat"><Icon name="x" size={20} /></button>
        <b>{title}</b>
      </div>

      {/* Kamera görüntüsü + çerçeve (destekleniyorsa) */}
      {supported && !code ? (
        <div className="m-scan-vf">
          <video ref={videoRef} playsInline muted />
          <span className="frame" />
          <span className="line" />
        </div>
      ) : !code ? (
        <div className="m-scan-nocam">
          <Icon name="qr" size={40} />
          <p>{camErr ?? "Bu cihazda otomatik tarama yok — kodu elle girin."}</p>
        </div>
      ) : null}

      {/* Çözüm sonucu */}
      {code ? (
        <div className="m-scan-result">
          {resolveQ.isLoading ? (
            <div className="m-scan-cap">Çözümleniyor… <span className="mono">{code}</span></div>
          ) : r?.type === "lot" ? (
            <div className="m-card m-card-p">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{r.lotCode}</span>
                <span className={`badge ${LOT_STATUS_TR[r.status]?.cls ?? "b-neutral"} no-dot`} style={{ marginLeft: "auto" }}>{LOT_STATUS_TR[r.status]?.label ?? r.status}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 6 }}>{r.itemName}</div>
              <div className="mono" style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>{r.itemCode}</div>
              <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                <div><div className="m-ovl">Bakiye</div><div className="mono" style={{ fontWeight: 700, marginTop: 2 }}>{fmtQty(r.qtyOnHand)} {r.uom}</div></div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button type="button" className="btn-md secondary" style={{ flex: 1 }} onClick={reset}>Tekrar Tara</button>
                {onLot ? (
                  <button type="button" className="btn-md primary" style={{ flex: 1 }} onClick={() => { stopCam(); const lot = { lotId: r.lotId, lotCode: r.lotCode, status: r.status }; onClose(); onLot(lot); }}><Icon name="checkCircle" size={15} />QC İşle</button>
                ) : (
                  <button type="button" className="btn-md primary" style={{ flex: 1 }} onClick={act}><Icon name="search" size={15} />İzlenebilirlik</button>
                )}
              </div>
            </div>
          ) : r?.type === "order" ? (
            <div className="m-card m-card-p">
              <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{r.orderCode}</span>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 6 }}>{r.customerName ?? "Sipariş"}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button type="button" className="btn-md secondary" style={{ flex: 1 }} onClick={reset}>Tekrar Tara</button>
                <button type="button" className="btn-md primary" style={{ flex: 1 }} onClick={act}><Icon name="list" size={15} />Siparişi Aç</button>
              </div>
            </div>
          ) : (
            <div className="m-card m-card-p" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "var(--neutral-600)" }}>Kod bulunamadı: <span className="mono">{code}</span></div>
              <div style={{ fontSize: 11.5, color: "var(--neutral-400)", marginTop: 4 }}>Lot (LOT-…) veya sipariş (SO-…) kodu okutun.</div>
              <button type="button" className="btn-md secondary" style={{ marginTop: 12, width: "100%" }} onClick={reset}>Tekrar Dene</button>
            </div>
          )}
        </div>
      ) : (
        <div className="m-scan-cap">Barkodu/QR'ı çerçeveye hizalayın</div>
      )}

      {/* Manuel giriş — her zaman erişilebilir */}
      {!code ? (
        <div className="m-scan-manual">
          <input inputMode="text" placeholder="veya kodu elle yaz (LOT-… / SO-…)" value={manual}
            onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }} aria-label="Kod" />
          <button type="button" className="btn-md primary" onClick={submitManual} disabled={!manual.trim()}><Icon name="search" size={16} />Bul</button>
        </div>
      ) : null}
    </div>
  );
}
